import { supabase } from "@/integrations/supabase/client";
import { PaginationResult } from "@/lib/pagination";
import { ApiService } from "@/services/api/ApiService";
import { DocumentFilters, DocumentWithMetadata, ListDocument } from "@/types/knowledge";

export const docContentBaseUrl =
  "https://s3.ap-southeast-2.amazonaws.com/fs.enterprisedna.co/pdfs/";

export class DocumentService {
  static async getSignedUrl(path: string): Promise<string> {
    // If path is already an absolute URL, return it as-is
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }

    // Otherwise, get a signed URL from Supabase
    const { data } = await supabase.storage
      .from("agent-documents")
      .createSignedUrl(path, 60 * 60 * 3);

    return data?.signedUrl || "";
  }

  /**
   * Documents attached to one of the current workspace's genies but owned by
   * a different user (typically an admin uploaded on the customer's behalf
   * and never reassigned it).
   */
  static async getDocumentsOutsideWorkspace(
    page: number = 1,
    pageSize: number = 50,
  ): Promise<PaginationResult<DocumentWithMetadata>> {
    try {
      const offset = (page - 1) * pageSize;

      const response = await ApiService.invoke({
        resource: "knowledge-base",
        action: "outside-workspace",
        data: {
          limit: pageSize,
          offset,
        },
      });

      const documents = response?.documents || [];
      const total = response?.count || 0;

      return {
        data: documents,
        page,
        pageSize,
        total,
        totalPages: Math.ceil(total / pageSize),
      };
    } catch (error) {
      console.error("Error fetching documents outside workspace:", error);
      return { data: [], page, pageSize, total: 0, totalPages: 0 };
    }
  }

  static async getKnowledgeBaseDocuments(
    filters?: DocumentFilters,
    adminMode: boolean = false,
    userId?: string,
  ): Promise<DocumentWithMetadata[]> {
    try {
      const response = await ApiService.invoke({
        resource: "knowledge-base",
        action: "all",
        data: {
          limit: 500,
          offset: 0,
          filters,
          adminMode,
          userId,
        },
      });

      return response?.documents || [];
    } catch (error) {
      console.error("Error fetching documents:", error);
      return [];
    }
  }

  static async getKnowledgeBaseDocumentsPaginated(
    userId: string,
    page: number = 1,
    pageSize: number = 10,
    filters?: DocumentFilters,
    adminMode: boolean = false,
  ): Promise<PaginationResult<DocumentWithMetadata>> {
    try {
      const offset = (page - 1) * pageSize;

      const response = await ApiService.invoke({
        resource: "knowledge-base",
        action: "all",
        data: {
          limit: pageSize,
          offset,
          filters,
          adminMode,
          userId,
        },
      });

      const documents = response?.documents || [];
      const total = response?.count || 0;
      const totalPages = Math.ceil(total / pageSize);

      return {
        data: documents,
        total,
        page,
        pageSize,
        totalPages,
        inKBCount: response?.inKBCount ?? undefined,
      };
    } catch (error) {
      console.error("Error fetching paginated documents:", error);
      return { data: [], total: 0, page, pageSize, totalPages: 0 };
    }
  }

  static async getDocumentsList(
    filters?: DocumentFilters,
    adminMode: boolean = false,
    userId?: string,
  ): Promise<ListDocument[]> {
    try {
      const response = await ApiService.invoke({
        resource: "knowledge-base",
        action: "list",
        data: {
          limit: 500,
          offset: 0,
          filters,
          adminMode,
          userId,
        },
      });

      return response?.documents || [];
    } catch (error) {
      console.error("Error fetching documents list:", error);
      return [];
    }
  }

  static async getDocumentsPaginatedList(
    page: number = 1,
    pageSize: number = 10,
    filters?: DocumentFilters,
    adminMode: boolean = false,
    userId?: string,
  ): Promise<PaginationResult<ListDocument>> {
    try {
      const offset = (page - 1) * pageSize;

      const response = await ApiService.invoke({
        resource: "knowledge-base",
        action: "list",
        data: {
          limit: pageSize,
          offset,
          filters,
          adminMode,
          userId,
        },
      });

      const documents = response?.documents || [];
      const total = response?.count || 0;
      const totalPages = Math.ceil(total / pageSize);

      return {
        data: documents,
        total,
        page,
        pageSize,
        totalPages,
      };
    } catch (error) {
      console.error("Error fetching paginated documents list:", error);
      return { data: [], total: 0, page, pageSize, totalPages: 0 };
    }
  }

  static async getKnowledgeBaseDocument(documentId: string): Promise<DocumentWithMetadata | null> {
    try {
      const response = await ApiService.invoke({
        resource: "knowledge-base",
        action: "get",
        id: documentId,
        data: {},
      });

      return response?.document || null;
    } catch (error) {
      console.error("Error fetching document:", error);
      return null;
    }
  }

  /**
   * Update an existing document in the knowledge base
   */
  static async updateKnowledgeBaseDocument(
    documentId: string,
    updates: {
      name?: string;
      status?: string;
      elDocId?: string;
      isPublic?: boolean;
      metadata?: any;
      folderId?: string | null;
      isPersonal?: boolean;
      userId?: string;
    },
  ): Promise<DocumentWithMetadata | null> {
    try {
      const updateData: any = {
        mode: "data",
      };

      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.status !== undefined) updateData.is_active = updates.status === "active";
      if (updates.elDocId !== undefined) updateData.el_doc_id = updates.elDocId;
      if (updates.isPublic !== undefined) updateData.is_public = updates.isPublic;
      if (updates.metadata !== undefined) updateData.metadata = updates.metadata;
      if (updates.folderId !== undefined) updateData.folder_id = updates.folderId;
      if (updates.isPersonal !== undefined) updateData.is_personal = updates.isPersonal;
      if (updates.userId !== undefined) updateData.user_id = updates.userId;

      const response = await ApiService.invoke({
        resource: "knowledge-base",
        action: "update",
        id: documentId,
        data: updateData,
      });

      return response?.document || null;
    } catch (error) {
      console.error("Error updating document:", error);
      return null;
    }
  }

  /**
   * Delete a document from the knowledge base
   */
  static async deleteKnowledgeBaseDocument(documentId: string): Promise<boolean> {
    try {
      const response = await ApiService.invoke({
        resource: "knowledge-base",
        action: "delete",
        id: documentId,
      });

      return response?.success ?? false;
    } catch (error) {
      console.error("Error in deleteKnowledgeBaseDocument:", error);
      return false;
    }
  }

  /**
   * Update document content by creating/updating the file in storage
   */
  static async updateDocumentContent(
    documentId: string,
    content: string,
    isOptimized: boolean = false,
  ): Promise<{ success: boolean; filePath?: string; signedUrl?: string }> {
    try {
      const response = await ApiService.invoke({
        resource: "knowledge-base",
        action: "update",
        id: documentId,
        data: {
          mode: "content",
          content,
          isOptimized,
        },
      });

      return {
        success: true,
        filePath: response?.storagePath,
        signedUrl: response?.signedUrl,
      };
    } catch (error) {
      console.error("Error updating document content:", error);
      return { success: false };
    }
  }

  /**
   * Sync document changes with agents (replace or remove from agents' knowledge bases)
   * @param oldDocId - The document ID to sync/remove
   * @param newDocId - Optional new document ID for replacement
   * @param preventDeletion - If true, skips deletion of oldDocId from ElevenLabs (default: false)
   */
  static async syncDocumentWithAgents(
    oldDocId: string,
    newDocId?: string,
  ): Promise<{ success: boolean; syncedAgents?: number; errors?: any[] }> {
    try {
      const response = await ApiService.invoke({
        resource: "knowledge-base",
        action: "sync-with-agents",
        data: {
          oldDocId,
          newDocId,
        },
      });

      return {
        success: response?.success ?? false,
        syncedAgents: response?.syncedAgents,
        errors: response?.errors,
      };
    } catch (error) {
      console.error("Error syncing document with agents:", error);
      return { success: false, errors: [{ error: String(error) }] };
    }
  }

  /**
   * Bulk update documents with the same updates applied to all
   */
  static async bulkUpdateDocuments(
    documentIds: string[],
    updates: {
      is_active?: boolean;
      is_public?: boolean;
      is_private?: boolean;
      folder_id?: string | null;
      consumer_genie_id?: string | null;
      status?: string;
      source_type?: string;
      user_id?: string;
    },
  ): Promise<{ success: boolean; updatedCount: number }> {
    try {
      const response = await ApiService.invoke({
        resource: "knowledge-base",
        action: "bulk-update",
        data: {
          documentIds,
          updates,
        },
      });

      return {
        success: response?.success ?? false,
        updatedCount: response?.updatedCount ?? 0,
      };
    } catch (error) {
      console.error("Error in bulkUpdateDocuments:", error);
      throw error;
    }
  }

  /**
   * Clone a document
   * @param documentId - The document ID to clone
   * @param name - Optional custom name for the cloned document
   * @param userId - Optional user ID for admin impersonation
   */
  static async cloneDocument(
    documentId: string,
    name?: string,
    userId?: string,
    agentId?: string,
  ): Promise<DocumentWithMetadata | null> {
    try {
      const data: any = {};
      if (name) data.name = name;
      if (userId) data.userId = userId;
      if (agentId) data.agentId = agentId;

      const response = await ApiService.invoke({
        resource: "knowledge-base",
        action: "clone",
        id: documentId,
        data,
      });

      return response?.document || null;
    } catch (error) {
      console.error("Error cloning document:", error);
      return null;
    }
  }

  /**
   * Detach documents from an agent's knowledge base
   * @param agentId - The agent ID to detach documents from
   * @param options.elDocIds - Array of ElevenLabs document IDs (used for agent KB filtering)
   * @param options.documentIds - Array of database document IDs (used for agent_document cleanup)
   */
  static async detachDocumentsFromAgent(
    agentId: string,
    options: { elDocIds?: string[]; documentIds?: string[] },
  ): Promise<{ success: boolean; detachedCount: number }> {
    try {
      console.log("Detaching documents from agent:", { agentId, options });
      const response = await ApiService.invoke({
        resource: "knowledge-base",
        action: "detach",
        data: {
          agentId,
          elDocIds: options.elDocIds,
          documentIds: options.documentIds,
        },
      });

      console.log("Detach response:", response);
      return {
        success: response?.success ?? false,
        detachedCount: response?.detachedCount ?? 0,
      };
    } catch (error) {
      console.error("Error detaching documents from agent:", error);
      throw error;
    }
  }

  /**
   * Consolidate multiple documents into single/multi-part files (both parsed and optimized)
   */
  static async consolidateDocuments({
    documentIds,
    prune = false,
    docName,
  }: {
    documentIds: string[];
    prune?: boolean;
    docName?: string;
  }): Promise<{
    success: boolean;
    consolidatedDocumentId?: string;
    error?: string;
    message?: string;
    parts?: number;
  }> {
    try {
      const response = await ApiService.callEdge("doc-upload", {
        action: "consolidate",
        documentIds,
        prune,
        docName,
      });

      return {
        success: response?.success ?? false,
        consolidatedDocumentId: response?.consolidatedDocumentId,
        error: response?.error,
        message: response?.message,
        parts: response?.parts,
      };
    } catch (error) {
      console.error("Error consolidating documents:", error);
      return {
        success: false,
        error: String(error),
      };
    }
  }
}
